import React, { useEffect, useMemo, useState } from "react";
import { Box } from '@mui/material';
import { useLocation } from "react-router-dom";
import axios from "axios";
import SubTitle from '../common/SubTitle';
import DynamicTable from '../common/DynamicTable';
import SearchFilter from "../common/SearchFilter";
import config from "../../config.json";
import { ACTIONS_COLUMN_ID } from "../../constants/dashboardConstants";

export default function StopSchedule() {
    // State management
    const [stopData, setStopData] = useState({});
    const [schedules, setSchedules] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedFilters, setSelectedFilters] = useState({
        repitation: '',
        vehicle: '',
        route: ''
    });

    // Get organization ID from localStorage
    const parsedData = JSON.parse(localStorage.getItem("userState"));
    const orgId = parsedData?.user?.organizationId;

    // API base URLs
    const STOP_BASE_URL = `${config.baseURL}/stop`;
    const SCHEDULE_BASE_URL = `${config.baseURL}/schedule`;
    const ROUTE_BASE_URL = `${config.baseURL}/route`;
    const VEHICLE_BASE_URL = `${config.baseURL}/vehicle`;

    // Get stop ID from location state
    const location = useLocation();
    const { id } = location.state || {};

    // Fetch stop data and related schedules
    useEffect(() => {
        const fetchStopData = async () => {
            if (!id) return;

            setIsLoading(true);
            try {
                // Fetch stop data
                const stopResponse = await axios.get(`${STOP_BASE_URL}/id`, {
                    params: { id },
                    withCredentials: true
                });
                setStopData(stopResponse.data);

                // Process the schedule data related to this stop
                if (stopResponse.data && stopResponse.data.stopSchedule) {
                    const enhancedSchedules = await enhanceScheduleData(stopResponse.data.stopSchedule);
                    setSchedules(enhancedSchedules);
                }
                console.log("Stop data retrieved:", stopResponse.data);
                console.log("Stop schedule array:", stopResponse.data.stopSchedule);
                setIsLoading(false);
            } catch (error) {
                console.error("Error fetching stop data:", error);
                setError(error);
                setIsLoading(false);
            }
        };

        fetchStopData();

    }, [id]);


    // Function to enhance schedule data with route and vehicle information
    const enhanceScheduleData = async (stopSchedules) => {
        try {
            const enhancedData = await Promise.all(
                stopSchedules.map(async (scheduleItem) => {
                    // Fetch schedule details
                    const scheduleRes = await axios.get(`${SCHEDULE_BASE_URL}/id`, {
                        params: { scheduleId: scheduleItem.scheduleId },
                        withCredentials: true,
                    });

                    // Fetch route details
                    const routeRes = await axios.get(`${ROUTE_BASE_URL}/id`, {
                        params: { id: scheduleRes.data.routeId },
                        withCredentials: true,
                    });

                    // Fetch vehicle details for each vehicle in the schedule
                    const vehiclePromises = scheduleRes.data.vehicleSchedule.map(
                        (scheduleVehicle) =>
                            axios.get(`${VEHICLE_BASE_URL}/id`, {
                                params: {
                                    vehicleId: scheduleVehicle.vehicleId,
                                    organizationId: orgId,
                                },
                                withCredentials: true,
                            })
                    );

                    const vehicleResponses = await Promise.all(vehiclePromises);
                    const vehicles = vehicleResponses.map((res) => res.data.vehicle);

                    // Create enhanced schedule record
                    return {
                        ...scheduleItem,
                        scheduleData: scheduleRes.data,
                        routeName: routeRes.data.name,
                        vehicleNames: vehicles.map(v => v.name).join(", "),
                        repitation: scheduleRes.data.repitation,
                        // Add any other needed fields
                        arrivalTime: scheduleItem.arrivalTime,
                        departureTime: scheduleItem.departureTime
                    };
                })
            );

            return enhancedData;
        } catch (error) {
            console.error("Error enhancing schedule data:", error);
            return [];
        }
    };

    // Filter configurations for the search filter component
    const filterConfig = [
        {
            id: 'repitation',
            label: 'Repetition',
            options: [
                { value: 'DAILY', label: 'Daily' },
                { value: 'WEEKLY', label: 'Weekly' },
                { value: 'MONTHLY', label: 'Monthly' }
            ]
        },
        {
            id: 'route',
            label: 'Route',
            options: [...new Set(schedules.map(s => s.routeName))].map(
                route => ({ value: route, label: route })
            )
        },
        {
            id: 'vehicle',
            label: 'Vehicle',
            options: [...new Set(schedules.flatMap(s =>
                s.vehicleNames.split(", ")
            ))].map(vehicle => ({ value: vehicle, label: vehicle }))
        }
    ];

    // Filter schedules based on search term and filters
    const filteredSchedules = useMemo(() => {
        let result = [...schedules];

        // Apply search filter
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            result = result.filter(schedule =>
                schedule.vehicleNames?.toLowerCase().includes(term) ||
                schedule.routeName?.toLowerCase().includes(term) ||
                schedule.repitation?.toLowerCase().includes(term)
            );
        }

        // Apply dropdown filters
        if (selectedFilters.repitation) {
            result = result.filter(schedule =>
                schedule.repitation === selectedFilters.repitation
            );
        }

        if (selectedFilters.route) {
            result = result.filter(schedule =>
                schedule.routeName === selectedFilters.route
            );
        }

        if (selectedFilters.vehicle) {
            result = result.filter(schedule =>
                schedule.vehicleNames.includes(selectedFilters.vehicle)
            );
        }

        return result;
    }, [schedules, searchTerm, selectedFilters]);

    // Handle search input change
    const handleSearchChange = (value) => {
        setSearchTerm(value);
    };

    // Handle filter changes
    const handleFilterChange = (filterValues) => {
        setSelectedFilters(filterValues);
    };

    // Define table headers
    // const headers = [
    //     'vehicles',
    //     'arrivalTime',
    //     'departureTime',
    //     'repitation',
    //     'routes',
    // ];

    const headers = [
        { key: "vehicles", value: 'vehicles' },
        { key: "arrivalTime", value: 'arrivalTime' },
        { key: "departureTime", value: 'departureTime' },
        { key: "repitation", value: 'repitation' },
        { key: "routes", value: 'routes' },
        { key: ACTIONS_COLUMN_ID, value: 'Actions' }
    ];

    // Convert schedule data to format expected by DynamicTable
    const tableData = filteredSchedules.map(schedule => ({
        id: schedule.scheduleId,
        routes: schedule.routeName,  // Changed from routeName to routes
        vehicles: schedule.vehicleNames,  // Changed from vehicleNames to vehicles
        arrivalTime: formatTime(schedule.arrivalTime),
        departureTime: formatTime(schedule.departureTime),
        repitation: schedule.repitation
    }));

    // Helper function to format time
    function formatTime(timeString) {
        if (!timeString) return "-";
        // Format time string as needed
        return timeString;
    }
    console.info(tableData)
    return (
        <>
            <SubTitle
                title="Stops Schedules"
                path="Stops > Monitor Stop > Stop Schedules"
            />
            <Box
                sx={{
                    display: "flex",
                    height: "60px",
                    justifyContent: "space-between",
                    padding: "12px 16px",
                    margin: "0px 40px",
                    backgroundColor: "#FFFFFF",
                    boxShadow: "0px 2px 5px 0px #00000040",
                }}
            >
                <Box
                    sx={{
                        fontWeight: 500,
                        fontFamily: 'Roboto',
                        fontSize: '20px',
                        lineHeight: '100%',
                        letterSpacing: '0%',
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <span style={{ color: '#414958', padding: '2px 6px', borderRadius: '2px' }}>
                        Stop:
                    </span>
                    <span style={{
                        fontWeight: 600,
                        fontFamily: 'Roboto',
                        fontSize: '20px',
                        lineHeight: '100%',
                        letterSpacing: '0%',
                        color: '#183059',
                        padding: '2px 6px',
                        borderRadius: '2px',
                        marginLeft: '4px'
                    }}>
                        {stopData.name || "-"}
                    </span>
                </Box>
                <Box
                    sx={{
                        fontWeight: 500,
                        fontFamily: 'Roboto',
                        fontSize: '20px',
                        lineHeight: '100%',
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <span style={{ color: '#414958', padding: '2px 6px', borderRadius: '2px' }}>
                        Stop ID:
                    </span>
                    <span style={{
                        fontWeight: 600,
                        fontFamily: 'Roboto',
                        fontSize: '20px',
                        lineHeight: '100%',
                        color: '#183059',
                        padding: '2px 6px',
                        borderRadius: '2px',
                        marginLeft: '4px'
                    }}>
                        {stopData.stopId || "-"}
                    </span>
                </Box>
            </Box>
            <Box sx={{
                margin: "20px 40px",
                borderRadius: "10px",
                padding: "24px",
                border: "1px solid #A5DCF3",
                boxShadow: "-2px 1px 5px 0px #00000040"
            }}>
                <SearchFilter
                    filters={filterConfig}
                    searchPlaceholder="Search..."
                    onSearchChange={handleSearchChange}
                    onFilterChange={handleFilterChange}
                />

                <DynamicTable
                    headers={headers}
                    data={tableData}
                    loading={isLoading}
                />
            </Box>
        </>
    );
}